import React, { useState, useRef, useEffect, useContext } from "react";
import ShareGameLinkModal from "../modals/ShareGameLinkModal";
import GameRoomContext from "../../context/GameRoomContext";
import UserContext  from "../../context/UserContext";
import SocketContext from "../../context/SocketContext";
import useGameBase from '../../hooks/useGameBase';
import { pidOf, currentClientId } from '../../utils/normalizePlayerId';

function GameRoomPlayers({ voiceChatUsers: propVoiceChatUsers, speakingUsers: propSpeakingUsers, onParticipantSelect }) {

  const { gameRoomData, getShareLink, gameRoomTypingPlayers: contextTypingPlayers } = useContext(GameRoomContext);
  const { user } = useContext(UserContext);

  const minPlayers = gameRoomData?.gameRoomConfigSchema?.minPlayers || 2;
  const maxPlayers = gameRoomData?.gameRoomConfigSchema?.maxPlayers || 2;
  const gameState = gameRoomData?.gameRoomGame?.gameState ||  {};
  const gameRoomPlayers = gameRoomData?.gameRoomPlayers || [];
  const roomId = gameRoomData?.gameRoomDetails?.roomId;
  const roomName = gameRoomData?.gameRoomDetails?.roomName || roomId || "";
  const voiceChatUsers = Array.isArray(propVoiceChatUsers) ? propVoiceChatUsers : (gameRoomData?.voiceChatUsers || []);
  const speakingUsers = Array.isArray(propSpeakingUsers) ? propSpeakingUsers : (gameRoomData?.speakingUsers || []);
  const gameRoomTypingPlayers = contextTypingPlayers || gameRoomData?.gameRoomTypingPlayers || [];
  


  // Ref for tracking previous currentPlayer
  const prevCurrentPlayer = useRef(null);
  // Ref for audio element
  const audioRef = useRef(null);

  // Play sound when it becomes your turn
  useEffect(() => {
    if (
      gameState?.currentPlayer === user.id &&
      prevCurrentPlayer.current !== gameState?.currentPlayer
    ) {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play();
      }
    }
    prevCurrentPlayer.current = gameState?.currentPlayer;
  }, [gameState?.currentPlayer, user.id]);

  let allowedPlayersLabel =
    minPlayers === maxPlayers
      ? `${minPlayers} Player${minPlayers > 1 ? "s" : ""}`
      : `${minPlayers}-${maxPlayers} Players`;

  // Sort players by descending score
  // Support cumulative delta leaderboards: when `gameState.cumulativeDelta` exists,
  // show cumulative delta (lower is better) and sort ascending. Otherwise fall back
  // to legacy score display (descending).
  const cumDeltaMap = gameState?.cumulativeDelta || null;
  const playersWithScore = gameRoomPlayers.map((p) => {
    const scoreFromMap = gameState?.scores?.[p.socketId];
    const scoreFromGamePlayers = gameState?.players?.find((gp) => gp.socketId === p.socketId || gp.userId === p.userId)?.score;
    const score = typeof scoreFromMap !== "undefined"
      ? scoreFromMap
      : (typeof scoreFromGamePlayers !== "undefined" ? scoreFromGamePlayers : (p.score ?? 0));
    const cumDelta = cumDeltaMap && typeof cumDeltaMap[p.socketId] !== 'undefined' ? cumDeltaMap[p.socketId] : null;
    return {
      ...p,
      score,
      cumDelta,
    };
  });
  const sortedPlayers = (cumDeltaMap)
    ? playersWithScore.sort((a, b) => {
        // players with null cumDelta should go to the end
        if (a.cumDelta == null && b.cumDelta == null) return 0;
        if (a.cumDelta == null) return 1;
        if (b.cumDelta == null) return -1;
        return a.cumDelta - b.cumDelta;
      })
    : playersWithScore.sort((a, b) => b.score - a.score);

  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const socket = useContext(SocketContext);
  const [invitablePlayers, setInvitablePlayers] = useState([]);

  // useGameBase provides tolerant lookup against current room players
  const { findPlayerById } = useGameBase({ gameState: gameRoomData?.gameRoomGame?.gameState, gamePlayers: gameRoomPlayers, user, socket, roomId });

  // (global invite handling is done in MainLayout)

  // Invite UI state
  const [playerFilter, setPlayerFilter] = useState("");
  const [selectedInvitees, setSelectedInvitees] = useState([]);
  // Use lobbyPlayers when available for invites, otherwise fallback to gameRoomPlayers
  const inviteSource = Array.isArray(invitablePlayers) && invitablePlayers.length > 0 ? invitablePlayers : [];
  const filteredPlayers = inviteSource.filter((p) =>
    p.username.toLowerCase().includes(playerFilter.toLowerCase())
  );

  // Fetch share link from parent-provided function when modal opens
  useEffect(() => {
    if (showShareModal && typeof getShareLink === "function" && roomId) {
      getShareLink(roomId).then((link) => {
        if (typeof link === "string") setShareUrl(link);
      });
      // Request lobby players for inviting
      try {
        socket?.emit("getAllConnectedPlayers", {}, (res) => {
          if (res && res.ok && Array.isArray(res.players)) {
            // Exclude current user and players already in the room.
            const myId = currentClientId(socket, user);
            setInvitablePlayers(res.players.filter((p) => {
              // skip players already in this room
              if (p.roomId === roomId) return false;
              // normalize the incoming player's id
              const candidatePid = pidOf(p) || p.username || null;
              if (!candidatePid) return false;
              // Exclude local client
              if (candidatePid === myId) return false;
              // Exclude players already listed in gameRoomPlayers (tolerant match)
              if (findPlayerById(candidatePid)) return false;
              return true;
            }));
          }
        });
      } catch (e) {}
    }
  }, [showShareModal, getShareLink, roomId, socket, user, findPlayerById]);

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div className="mb-4 w-full px-2">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm italic text-gray-900 dark:text-gray-100 font-semibold">
          {allowedPlayersLabel}
        </h2>
        <button
          className="px-3 py-1 rounded bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold shadow-sm"
          onClick={() => setShowShareModal(true)}
        >
          Share Link
        </button>
      </div>
      <ShareGameLinkModal
        show={showShareModal}
        onClose={() => setShowShareModal(false)}
        shareUrl={shareUrl}
        copied={copied}
        handleCopy={handleCopy}
        playerFilter={playerFilter}
        setPlayerFilter={setPlayerFilter}
        filteredPlayers={filteredPlayers}
        selectedInvitees={selectedInvitees}
        setSelectedInvitees={setSelectedInvitees}
        onSendInvite={async () => {
          if (!roomId) return;
          // Ensure we have the share link
          let link = shareUrl;
          if (!link && typeof getShareLink === "function") {
            link = await getShareLink(roomId);
            setShareUrl(link || "");
          }
          // Emit server event to send invites
          try {
            socket.emit("sendLobbyInvite", { roomId, to: selectedInvitees, roomName: roomName, message: `Hey, come join my room!` }, (res) => {
              if (res && res.ok) {
                setShowShareModal(false);
              }
            });
          } catch (e) {}
        }}
        onClear={() => { setSelectedInvitees([]); setPlayerFilter(""); }}
      />
  {/* incoming invites are handled globally by MainLayout */}
      <div className="flex flex-col gap-1">
        {sortedPlayers.map((p) => {
          const showTyping =
            gameRoomTypingPlayers.includes(p.username) &&
            p.username !== user.username;
          const isCurrentUser = p.username === user.username;
          const isCurrentTurn =
            gameState?.currentPlayer &&
            (p.id === gameState.currentPlayer ||
              p.username === gameState.currentPlayer);
          const isConnected = typeof p.connected === 'undefined' ? true : !!p.connected;
          return (
            <button
              type="button"
              key={p.userId || p.socketId}
              onClick={() => {
                if (typeof onParticipantSelect === "function") {
                  onParticipantSelect({ ...p, role: "player" });
                }
              }}
              className={
                "flex w-full items-center gap-2 p-1 rounded-md border border-gray-200 dark:border-gray-700 shadow-sm text-left transition hover:border-blue-300 hover:bg-blue-50/60 dark:hover:border-blue-700 dark:hover:bg-blue-950/40 " +
                (isCurrentTurn
                  ? isCurrentUser
                    ? "animate-turn-bg"
                    : "bg-blue-50 dark:bg-blue-800"
                  : "bg-gray-50 dark:bg-gray-800")
              }
            >
              <span
                className={
                  (isCurrentUser
                    ? "font-bold text-blue-700 dark:text-blue-300"
                    : "font-bold text-gray-900 dark:text-gray-100") +
                  " w-8 h-8 flex items-center justify-center text-xl rounded-full border"
                }
                style={{
                  backgroundColor: p.color || "#e57373",
                  borderColor: p.color,
                  marginRight: "4px",
                  opacity: isConnected ? 1 : 0.45,
                }}
              >
                {typeof p.icon === "string" ? p.icon : "😀"}
              </span>
              <span className="font-semibold text-gray-800 dark:text-gray-100 text-m">
                {typeof p.username === "string" ? p.username : ""}
                {isCurrentUser && " (You)"}
                {p.isHost && (
                  <span title="Host" className="text-yellow-500 text-lg ml-1">
                    &#x1F451;
                  </span>
                )}
                {!isConnected && (
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 italic">(Disconnected)</span>
                )}
                {/* Typing dots right after name/host icon */}
                {showTyping && (
                  <span className="typing-dots ml-1" title="Typing...">
                    <span className="typing-dot">•</span>
                    <span className="typing-dot dot2">•</span>
                    <span className="typing-dot dot3">•</span>
                  </span>
                )}
              </span>
              {(() => {
                // Only consider the player's socketId when deciding voice/chat status.
                // voiceChatUsers and speakingUsers are populated with socket ids from the signaling layer.
                const inVoice = isConnected && p.socketId && voiceChatUsers.includes(p.socketId);
                const isSpeaking = isConnected && p.socketId && speakingUsers.includes(p.socketId);
                return inVoice ? (
                  <span title="In voice chat" className="ml-1">
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill={isSpeaking ? "#22c55e" : "#888"}
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M12 3a4 4 0 0 1 4 4v4a4 4 0 0 1-8 0V7a4 4 0 0 1 4-4zm6 8a6 6 0 1 1-12 0" />
                      <path
                        d="M19 15v2a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3v-2"
                        stroke="#888"
                        strokeWidth="2"
                        fill="none"
                      />
                    </svg>
                  </span>
                ) : null;
              })()}
              {gameRoomData?.gameRoomGame?.gameState?.gameClass !== 'WheelOfDestiny' && (
                <span
                  className="ml-auto text-sm font-mono text-right text-gray-700 dark:text-gray-200"
                  style={{ minWidth: "3.5rem", display: "inline-block" }}
                >
                  {typeof p.cumDelta === 'number' ? (p.cumDelta/1000).toFixed(2)+'s' : (typeof p.score === 'number' ? p.score : '')}
                </span>
              )}
              <style>{`
        @keyframes turnBgPulse {
          0% { background-color: #f9fafb; }
          50% { background-color: #85b6edff; }
          100% { background-color: #f9fafb; }
        }
        .animate-turn-bg {
          background-color: #f9fafb;
          animation: turnBgPulse 1.5s infinite alternate;
        }
        .dark .animate-turn-bg {
          background-color: #1e293b;
          animation: turnBgPulseDark 1.5s infinite alternate;
        }
        @keyframes turnBgPulseDark {
          0% { background-color: #1e293b; }
          50% { background-color: #1b67d2ff; }
          100% { background-color: #1e293b; }
        }
      `}</style>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default GameRoomPlayers;
